import { Component } from '@theme/component';

/**
 * Server-rendered stand-in for the Yotpo star rating at the top of the PDP (blocks/sku.liquid).
 *
 * The real widget only hydrates once the Yotpo reviews loader runs, and that loader waits for the
 * first interaction or 6s (snippets/scripts.liquid). The stand-in shows the same stars from first
 * paint and Yotpo's widget stays hidden underneath (product-information-content.liquid). A click must
 * still open Yotpo's own reviews-summary dialog, so it is forwarded to the hidden widget button —
 * at once if the widget has rendered, otherwise as soon as it does (the click itself triggers the
 * loader). If it has not rendered within WAIT_MS the forward is cancelled — a dialog popping up
 * seconds after the shopper moved on is worse than nothing — and the click scrolls to the reviews.
 */
const WAIT_MS = 6000;

class RatingSsrComponent extends Component {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | undefined} */
  #timeout;

  open() {
    const rating = this.closest('.sl-product__rating');
    if (!rating) return;

    const widgetButton = () => rating.querySelector('.yotpo-widget-instance .yotpo-sr-bottom-line-summary');
    const ready = widgetButton();
    if (ready instanceof HTMLElement) {
      ready.click();
      return;
    }

    this.#stopWaiting();
    this.#observer = new MutationObserver(() => {
      const button = widgetButton();
      if (!(button instanceof HTMLElement)) return;
      this.#stopWaiting();
      button.click();
    });
    this.#observer.observe(rating, { childList: true, subtree: true });
    this.#timeout = window.setTimeout(() => {
      this.#stopWaiting();
      this.closest('main')?.querySelector('.yotpo-reviews-main-widget, .sl-reviews-ssr')?.scrollIntoView({ behavior: 'smooth' });
    }, WAIT_MS);
  }

  #stopWaiting() {
    this.#observer?.disconnect();
    this.#observer = null;
    window.clearTimeout(this.#timeout);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopWaiting();
  }
}

if (!customElements.get('sl-rating-ssr-component')) {
  customElements.define('sl-rating-ssr-component', RatingSsrComponent);
}
